import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import styled from '@emotion/styled';
import toast from '../../utils/toast';
import {
  m,
  Presence,
  scrimMotion,
  useOverlayMotion,
  StateSwitch,
  StatusLabel,
} from '../../motion';
// `/pure`, not the bare entry point: that one injects Stripe's <script> the
// moment this module is imported, card or no card (stripe-import.guard.test.ts).
import { loadStripe } from '@stripe/stripe-js/pure';
import type { Stripe } from '@stripe/stripe-js';
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { apiRoute } from '../../constants/api-constants';
import { compedRefusal } from '../../api/plan-client';
import { ModalPortal } from '../modal/modal-portal';
import { ConfirmDialog } from '../modal/confirm-dialog';
import { useDialogFocus } from '../hooks/use-dialog-focus';
import {
  palette,
  radius,
  shadow,
  spacing,
  typography,
  zIndex,
} from '../../style-utils/theme';
import { down } from '../../style-utils/media-queries';
import {
  Field,
  FieldRow,
  Label,
  StripeField,
  FieldError,
  FieldSkeleton,
  SkeletonButton,
  SkeletonGroup,
  SkeletonLine,
  InfoBanner,
  SummaryBox,
  SummaryRow,
  SummaryValue,
  SummaryTotal,
  ButtonRow,
  PrimaryButton,
  SecondaryButton,
} from '../onboarding/onboarding.styled';

// ---------------------------------------------------------------------------
// THE ONE PLACE THE FRONTEND TALKS TO STRIPE
// ---------------------------------------------------------------------------
// Before this module, `onboarding/signup/signup-page.tsx` was the only file in
// the frontend that imported Stripe at all: it fetched the publishable key,
// mounted the split card Elements, and confirmed a SetupIntent. Signup is still
// the ONLY working payment path in the product, so everything here is lifted
// from it deliberately rather than rewritten, and signup now consumes this
// module instead of owning a second copy.
//
// `grep -rln "@stripe/react-stripe-js" src` must find ONLY this file. If it
// finds a second one, the card surface has forked again.
//
// TWO INTENT KINDS, AND WHY THAT IS NOT OPTIONAL
// ----------------------------------------------
// A SetupIntent SAVES a card. It does not charge it. `confirmCardSetup`
// resolving successfully means "we can bill this card later", never "money
// moved". The frontend had ZERO `confirmCardPayment` usages, so a modal that
// only knew `confirmCardSetup` would report success on a purchase where nothing
// was paid — which is exactly the failure the billing review flagged.
//
// So the caller declares which intent it produced (`mode` on the value returned
// by `prepareIntent`), this module calls the matching confirm function, and the
// success callback is handed `confirmed: 'confirmCardSetup' | 'confirmCardPayment'`
// so a caller can ASSERT which one actually ran instead of assuming.
//
// `confirmCardPayment` is NEW CAPABILITY in this frontend. It is implemented and
// typed here; no caller uses it yet, because no server route mints a
// PaymentIntent today. Whoever adds one must pass `mode: 'payment'` — passing
// `'setup'` for a purchase would save a card and report success while the
// customer was never charged.
//
// A CLIENT SECRET IS NEVER ASSUMED TO EXIST
// -----------------------------------------
// `createTrialSubscriptionSetup` throws `NO_SETUP_INTENT` whenever Stripe does
// not return a `pending_setup_intent` — which is what a subscription created
// with `payment_behavior: 'default_incomplete'` and NO trial does, because that
// produces a PaymentIntent on the invoice instead. `prepareIntent` returning
// nothing, or returning a blank secret, is therefore a REAL server state and is
// handled as an error the user can see, not as an unchecked dereference.

/** Which Stripe confirmation a client secret requires. */
export type StripeIntentMode = 'setup' | 'payment';

/** What a caller's `prepareIntent` must produce. */
export interface StripeIntent {
  /**
   * 'setup'   — a SetupIntent. Saves a card. NOTHING IS CHARGED.
   * 'payment' — a PaymentIntent. Money moves.
   */
  mode: StripeIntentMode;
  clientSecret: string;
}

/**
 * Handed to `onConfirmed`. `confirmed` names the Stripe call that actually ran,
 * so a purchase flow can assert it was NOT handed a saved-card result.
 */
export interface StripeCardConfirmation {
  confirmed: 'confirmCardSetup' | 'confirmCardPayment';
  mode: StripeIntentMode;
  intentId: string | null;
  /** Stripe's own status, e.g. 'succeeded' / 'requires_capture'. */
  status: string | null;
  paymentMethodId: string | null;
}

export interface StripeSummaryLine {
  label: string;
  value: string;
  /** Renders as the emphasised bottom row. */
  total?: boolean;
}

// Stripe split-Element styling, matched to the app's <Input> (ink text, muted
// placeholder, Inter 16px, coral error). The border/focus ring live on our
// <StripeField> wrapper, so the Element itself is just the text.
export const CARD_ELEMENT_STYLE = {
  base: {
    color: '#12292C',
    fontFamily: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
    fontSize: '16px',
    fontSmoothing: 'antialiased',
    '::placeholder': { color: '#93A6A8' },
  },
  invalid: { color: '#F0564A', iconColor: '#F0564A' },
  complete: { color: '#12292C' },
};

// NO WEB FONT IN THE STRIPE IFRAME (privacy & security review, 2026-09-14).
// This used to hand Stripe a fonts.googleapis.com stylesheet, so opening the card
// form sent the visitor's IP address to Google — the one font request left after
// the app's fonts were self-hosted (public/fonts). Pointing it at our own
// /fonts/fonts.css would need CORS headers on the font files, because the iframe
// runs on Stripe's origin. The card fields therefore render in the system face
// already named in CARD_ELEMENT_STYLE's fontFamily — two input fields, not the page.
export const STRIPE_FONTS: never[] = [];

// A dedicated instance rather than the default one. The default axios carries
// the app's auth interceptors (401 -> refresh -> retry), and this module is
// imported by BOTH a public unauthenticated page and an authenticated dashboard
// page — it must not depend on `publicApi` or on a session existing.
// `withCredentials` costs nothing on a GET and keeps it identical to every other
// call the app makes. Built on first use rather than at import, so merely
// importing this module never touches axios.
let configClient: {
  get: (url: string) => Promise<{ data?: { publishable_key?: string | null } }>;
} | null = null;

const client = () => {
  if (!configClient) {
    configClient = axios.create({
      baseURL: apiRoute as string,
      withCredentials: true,
    });
  }

  return configClient;
};

// Module-level so the key is fetched and Stripe.js is loaded ONCE per page load,
// however many times a modal is opened. A failure clears the cache so the next
// open retries instead of being permanently stuck on "unavailable".
let stripeLoad: Promise<Stripe | null> | null = null;

/**
 * START FETCHING STRIPE NOW, for a screen that already knows a card step is
 * coming. Stripe.js is no longer loaded just because this file was imported
 * (that put it on every page, card or no card), so without this the card step
 * only begins the download when it opens and sits on "Loading secure payment…"
 * for as long as that takes — four seconds and more on a slow connection,
 * measured 2026-10-13. Call it when the answer "a card will be asked for" is
 * known, never before: under free signup it must not be called at all.
 * Safe to call more than once; a failure is left for the card step to report.
 */
export const preloadStripe = (): void => {
  loadStripeOnce().catch(() => undefined);
};

const loadStripeOnce = (): Promise<Stripe | null> => {
  if (!stripeLoad) {
    stripeLoad = client()
      .get('/stripe/config')
      .then(res => {
        const key = res.data && res.data.publishable_key;

        return key ? loadStripe(key) : null;
      })
      .catch(err => {
        stripeLoad = null;
        throw err;
      });
  }

  return stripeLoad;
};

/**
 * Test-only. The cache above is per page load by design, which makes a suite
 * that renders the panel more than once share whatever the first render
 * fetched — so a "no publishable key" case would silently pass with the key the
 * previous test resolved. Nothing in the app calls this.
 */
export const __resetStripeConfigCacheForTests = () => {
  stripeLoad = null;
};

/**
 * `{ stripePromise, ready }`. `ready` with a null promise is the supported
 * "no publishable key / provider unreachable" state, not an error — every
 * consumer renders its own explanation instead of a card form that could not
 * work.
 */
export const useStripeConfig = (enabled = true) => {
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // A confirm-only flow has nothing to confirm, so it must not fetch the key
    // or pull Stripe.js down. Guarded here rather than by not calling the hook,
    // because a conditional hook is not a thing.
    if (!enabled) return undefined;

    let alive = true;
    loadStripeOnce()
      .then(instance => {
        // `loadStripe` resolves to null when Stripe.js itself fails to load, so
        // an instance-less promise is treated the same as a missing key.
        if (alive && instance) setStripePromise(Promise.resolve(instance));
      })
      .catch(() => {
        /* handled by the `ready && !stripePromise` branch below */
      })
      .finally(() => {
        if (alive) setReady(true);
      });

    return () => {
      alive = false;
    };
  }, [enabled]);

  return { stripePromise, ready };
};

// Reads a server message out of an axios error without importing AxiosError, so
// this module stays usable from any client instance. A complimentary account's
// refusal (COMPED_ACCOUNT) is named for what it is first.
const serverMessage = (err: unknown): string => {
  const e = err as {
    response?: { data?: { message?: string; error?: string } };
  };

  return (
    compedRefusal(err) ||
    (e && e.response && e.response.data && e.response.data.message) ||
    (e && e.response && e.response.data && e.response.data.error) ||
    (err instanceof Error ? err.message : '') ||
    ''
  );
};

export interface StripeCardFormProps {
  /**
   * FALSE renders no card fields and no Elements at all, and Submit calls
   * `onSubmit`. Use it when the server has confirmed there is nothing to
   * confirm — a card already on file. It is a separate callback from
   * `onConfirmed` on purpose: a caller can never mistake "no card was
   * confirmed" for "a card was confirmed".
   */
  collectCard: boolean;
  /**
   * Called AT MOST ONCE, on the first submit, to create the intent server-side.
   * Its result is cached, so a declined card retries the confirmation WITHOUT
   * re-creating anything — that is what stops a retry from 409ing on the
   * already-created account in signup. Must reject, or return a blank secret,
   * rather than inventing one.
   */
  prepareIntent?: () => Promise<StripeIntent | null | undefined>;
  /** Runs only after a real Stripe confirmation. */
  onConfirmed?: (result: StripeCardConfirmation) => void | Promise<void>;
  /** Runs on submit when `collectCard` is false. No card, no money. */
  onSubmit?: () => void | Promise<void>;
  billingDetails?: { name?: string; email?: string };
  summaryRows?: StripeSummaryLine[];
  /** Rendered above the card fields — a server refusal, a caveat, a warning. */
  notice?: React.ReactNode;
  /** Extra fields, rendered under the card fields and above the error line. */
  children?: React.ReactNode;
  submitLabel: React.ReactNode;
  busyLabel: React.ReactNode;
  cancelLabel?: React.ReactNode;
  onCancel?: () => void;
  submitDisabled?: boolean;
  /**
   * Return true to say "I handled this" and suppress the inline message and the
   * toast — signup uses it to route a 409 to its own email step.
   */
  onError?: (err: unknown) => boolean | void;
  fallbackErrorMessage?: string;
  /**
   * Fires with true once the intent exists. Once something has been created
   * server-side the caller must not be able to navigate away and lose it.
   */
  onLockChange?: (locked: boolean) => void;
  /** True once ANY card field has something in it. The dialog uses this to ask
   *  before throwing the entry away; no card data crosses this callback, only
   *  whether Stripe considers the fields empty. */
  onDirtyChange?: (dirty: boolean) => void;
}

// `useStripe()` and `useElements()` THROW outside an <Elements> provider
// ("Could not find Elements context"), and a confirm-only flow deliberately
// renders no provider — there is nothing to confirm, so there is no reason to
// load Stripe.js. The hooks therefore live in a wrapper that is only ever
// mounted inside <Elements>, and the body below takes what it needs as props.
// Calling the hooks unconditionally in here would crash every confirm-only
// caller at first render.
type CardFormBodyProps = StripeCardFormProps & {
  stripe: ReturnType<typeof useStripe>;
  elements: ReturnType<typeof useElements>;
};

const CardFormBody: React.FC<CardFormBodyProps> = ({
  stripe,
  elements,
  collectCard,
  prepareIntent,
  onConfirmed,
  onSubmit,
  billingDetails,
  summaryRows,
  notice,
  children,
  submitLabel,
  busyLabel,
  cancelLabel,
  onCancel,
  submitDisabled,
  onError,
  fallbackErrorMessage = 'Something went wrong. Please try again.',
  onLockChange,
  onDirtyChange,
}) => {
  const [busy, setBusy] = useState(false);
  const [cardError, setCardError] = useState('');
  const [locked, setLocked] = useState(false);
  // The intent survives a decline so a retry re-confirms the SAME intent
  // instead of creating a second one.
  const intentRef = useRef<StripeIntent | null>(null);

  const fail = (err: unknown) => {
    const handled = onError ? onError(err) : false;
    if (handled !== true) {
      const message = serverMessage(err) || fallbackErrorMessage;
      setCardError(message);
      toast.error(message);
    }
    setBusy(false);
  };

  const submit = async () => {
    if (busy) return;

    // No card to collect: one callback, and it is NOT onConfirmed.
    if (!collectCard) {
      setBusy(true);
      setCardError('');
      try {
        if (onSubmit) await onSubmit();
        setBusy(false);
      } catch (err) {
        fail(err);
      }

      return;
    }

    if (!stripe || !elements) return;
    setBusy(true);
    setCardError('');
    try {
      // 1. Create the intent, once.
      if (!intentRef.current) {
        if (!prepareIntent) {
          throw new Error(
            'This payment cannot be completed yet — no payment setup was provided.'
          );
        }
        const prepared = await prepareIntent();
        // NEVER assume a client secret came back. `NO_SETUP_INTENT` is a real
        // server state: a subscription created without a trial has no
        // `pending_setup_intent` at all, and the route 500s rather than
        // returning one.
        if (!prepared || !prepared.clientSecret) {
          throw new Error(
            'The payment provider did not return a payment setup. Nothing was charged — please try again.'
          );
        }
        if (prepared.mode !== 'setup' && prepared.mode !== 'payment') {
          throw new Error('Unrecognised payment setup. Please try again.');
        }
        intentRef.current = prepared;
        setLocked(true);
        if (onLockChange) onLockChange(true);
      }

      const intent = intentRef.current;
      const cardNumber = elements.getElement(CardNumberElement);
      if (!cardNumber) {
        setCardError('The card form is not ready yet. Please try again.');
        setBusy(false);

        return;
      }
      const paymentMethod = {
        card: cardNumber,
        billing_details: {
          name: (billingDetails && billingDetails.name) || undefined,
          email: (billingDetails && billingDetails.email) || undefined,
        },
      };

      // 2. Confirm — the branch the whole module exists for.
      let result: StripeCardConfirmation;
      if (intent.mode === 'payment') {
        const { error, paymentIntent } = await stripe.confirmCardPayment(
          intent.clientSecret,
          { payment_method: paymentMethod }
        );
        if (error) {
          // Keep the intent so a retry re-confirms it.
          setCardError(
            error.message ||
              'Your card could not be charged. Please check the details.'
          );
          setBusy(false);

          return;
        }
        result = {
          confirmed: 'confirmCardPayment',
          mode: 'payment',
          intentId: (paymentIntent && paymentIntent.id) || null,
          status: (paymentIntent && paymentIntent.status) || null,
          paymentMethodId:
            paymentIntent && typeof paymentIntent.payment_method === 'string'
              ? paymentIntent.payment_method
              : null,
        };
      } else {
        const { error, setupIntent } = await stripe.confirmCardSetup(
          intent.clientSecret,
          { payment_method: paymentMethod }
        );
        if (error) {
          setCardError(
            error.message ||
              'Your card could not be saved. Please check the details.'
          );
          setBusy(false);

          return;
        }
        result = {
          confirmed: 'confirmCardSetup',
          mode: 'setup',
          intentId: (setupIntent && setupIntent.id) || null,
          status: (setupIntent && setupIntent.status) || null,
          paymentMethodId:
            setupIntent && typeof setupIntent.payment_method === 'string'
              ? setupIntent.payment_method
              : null,
        };
      }

      // 3. Hand back, saying which confirmation ran.
      if (onConfirmed) await onConfirmed(result);
      setBusy(false);
    } catch (err) {
      fail(err);
    }
  };

  /* The three card fields each report their own emptiness, so the dialog is
     dirty when ANY of them holds something. Kept in a ref beside the state so a
     second event for the same field does not re-notify the parent. */
  const filled = useRef<Record<string, boolean>>({});
  const onFieldChange = (field: string) => (event?: { empty?: boolean }) => {
    setCardError('');
    // Stripe always sends an event; the optional read is because a field that
    // reports nothing must count as EMPTY rather than crash the handler that
    // guards the reader's card entry.
    filled.current[field] = event ? !event.empty : false;
    if (onDirtyChange)
      onDirtyChange(Object.values(filled.current).some(Boolean));
  };

  return (
    <>
      {summaryRows && summaryRows.length > 0 && (
        <SummaryBox>
          {summaryRows
            .filter(r => !r.total)
            .map(r => (
              <SummaryRow key={r.label}>
                <span>{r.label}</span>
                <SummaryValue>{r.value}</SummaryValue>
              </SummaryRow>
            ))}
          {summaryRows
            .filter(r => r.total)
            .map(r => (
              <SummaryTotal key={r.label}>
                <span>{r.label}</span>
                <span>{r.value}</span>
              </SummaryTotal>
            ))}
        </SummaryBox>
      )}

      {notice}

      {collectCard && (
        <>
          <Field>
            <Label htmlFor="cc-number">Card number</Label>
            <StripeField hasError={!!cardError}>
              <CardNumberElement
                id="cc-number"
                options={{
                  style: CARD_ELEMENT_STYLE,
                  placeholder: '4242 4242 4242 4242',
                }}
                onChange={onFieldChange('number')}
              />
            </StripeField>
          </Field>

          <FieldRow>
            <Field>
              <Label htmlFor="cc-exp">Expiry</Label>
              <StripeField hasError={!!cardError}>
                <CardExpiryElement
                  id="cc-exp"
                  options={{ style: CARD_ELEMENT_STYLE }}
                  onChange={onFieldChange('expiry')}
                />
              </StripeField>
            </Field>
            <Field>
              <Label htmlFor="cc-cvc">CVC</Label>
              <StripeField hasError={!!cardError}>
                <CardCvcElement
                  id="cc-cvc"
                  options={{ style: CARD_ELEMENT_STYLE }}
                  onChange={onFieldChange('cvc')}
                />
              </StripeField>
            </Field>
          </FieldRow>
        </>
      )}

      {children}

      <FieldError message={cardError || undefined} />

      <ButtonRow>
        {cancelLabel !== undefined && onCancel && (
          <SecondaryButton
            type="button"
            onClick={onCancel}
            disabled={busy || locked}
          >
            {cancelLabel}
          </SecondaryButton>
        )}
        <PrimaryButton
          type="button"
          onClick={submit}
          disabled={busy || !!submitDisabled}
        >
          <StatusLabel
            state={busy ? 'busy' : 'idle'}
            labels={{ idle: submitLabel, busy: busyLabel }}
          />
        </PrimaryButton>
      </ButtonRow>
    </>
  );
};

/**
 * The card-collecting form. MUST be rendered inside <Elements> — it calls the
 * Stripe hooks. `StripeCardPanel` is what guarantees that.
 */
export const StripeCardForm: React.FC<StripeCardFormProps> = props => {
  const stripe = useStripe();
  const elements = useElements();

  return <CardFormBody {...props} stripe={stripe} elements={elements} />;
};

export interface StripeCardPanelProps extends StripeCardFormProps {
  /** Shown while the publishable key is still being fetched. */
  loadingContent?: React.ReactNode;
  /**
   * Shown when there is no publishable key, or the provider could not be
   * reached. There is no card-free fallback to offer here — say so plainly
   * rather than presenting a form that cannot work.
   */
  noKeyContent?: React.ReactNode;
}

/* WHAT THE CARD FORM LOOKS LIKE BEFORE IT EXISTS. While the publishable key is
   fetched the panel used to show one banner where three 48px fields, a summary
   and a button row were about to land, so the card jumped when Stripe arrived.
   This reserves that footprint: the summary lines (when there is a summary),
   the card number field, the expiry/CVC row and the buttons. */
const PaymentSkeleton: React.FC<{
  withSummary: boolean;
  /** The caller's extra fields (signup's promo code) sit under the card row;
   *  one more field-shaped box stands in for them. */
  withChildren: boolean;
}> = ({ withSummary, withChildren }) => (
  <SkeletonGroup label="Loading secure payment">
    {withSummary && (
      <SummaryBox aria-hidden="true">
        <SkeletonLine w="62%" />
        <SkeletonLine w="54%" />
        <SkeletonLine w="70%" />
        <SkeletonLine w="58%" />
      </SummaryBox>
    )}
    <InfoBanner>Loading secure payment…</InfoBanner>
    <FieldSkeleton />
    <FieldRow aria-hidden="true">
      <FieldSkeleton />
      <FieldSkeleton />
    </FieldRow>
    {withChildren && <FieldSkeleton />}
    <ButtonRow aria-hidden="true">
      <SkeletonButton style={{ flex: 1 }} />
      <SkeletonButton style={{ flex: 1 }} />
    </ButtonRow>
  </SkeletonGroup>
);

/**
 * INLINE card surface — no scrim, no dialog. This is what the signup wizard
 * renders inside its own step card. `StripeCardModal` is the same thing in a
 * dialog.
 */
export const StripeCardPanel: React.FC<StripeCardPanelProps> = ({
  loadingContent,
  noKeyContent,
  ...formProps
}) => {
  const { stripePromise, ready } = useStripeConfig(formProps.collectCard);

  // Nothing to confirm means nothing to load: no /stripe/config, no Stripe.js,
  // and NO <Elements> — so the body is rendered directly, without the hooks
  // that would throw for want of a provider.
  if (!formProps.collectCard) {
    return <CardFormBody {...formProps} stripe={null} elements={null} />;
  }

  // Three fills of the same box — the placeholder, the form, or the
  // explanation — crossfading, so the surrounding card never jumps.
  const state = !ready ? 'loading' : !stripePromise ? 'nokey' : 'ready';

  return (
    <StateSwitch state={state}>
      {state === 'loading' &&
        (loadingContent ?? (
          <PaymentSkeleton
            withSummary={!!formProps.summaryRows?.length}
            withChildren={!!formProps.children}
          />
        ))}
      {state === 'nokey' &&
        (noKeyContent ?? (
          <InfoBanner tone="warning">
            Payments are temporarily unavailable — our payment provider
            isn&rsquo;t reachable from this page right now. Nothing has been
            charged, so please try again shortly.
          </InfoBanner>
        ))}
      {state === 'ready' && stripePromise && (
        <Elements stripe={stripePromise} options={{ fonts: STRIPE_FONTS }}>
          <StripeCardForm {...formProps} />
        </Elements>
      )}
    </StateSwitch>
  );
};

/* Portaled to <body> for the same reason every other modal is: an animated
 * ancestor creates a transform containing block that would trap a fixed
 * overlay, and leave the sticky header undimmed above the scrim. */
const Scrim = m.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.md};
  background: ${palette.overlay};
  overflow-y: auto;

  ${down('sm')} {
    align-items: flex-end;
    padding: 0;
  }
`;

const Card = m.div`
  width: 100%;
  max-width: 460px;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.lg};
  padding: ${spacing.lg};

  ${down('sm')} {
    max-width: none;
    max-height: calc(100vh - ${spacing.sm});
    max-height: calc(100dvh - ${spacing.sm});
    overflow-y: auto;
    border-radius: ${radius.lg} ${radius.lg} 0 0;
    padding: ${spacing.md} ${spacing.sm};
  }
`;

const Title = styled.h2`
  margin: 0 0 ${spacing.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.bold};
  letter-spacing: -0.014em;
  color: ${palette.ink};
`;

const Description = styled.p`
  margin: 0 0 ${spacing.md};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

export interface StripeCardModalProps extends StripeCardPanelProps {
  open: boolean;
  title: string;
  description?: React.ReactNode;
  /** Scrim click / Escape. Suppressed while the form is locked or working. */
  onDismiss: () => void;
}

/**
 * The dialog form of the same surface, for the dashboard.
 *
 * NOTE for whoever wires a purchase to this: the Stripe iframe inside a
 * portaled dialog inside the dashboard shell is a combination signup never
 * exercises. It is portaled for the reasons above, so no ancestor transform can
 * trap it, but confirm it in a browser before shipping a money path on it.
 */
export const StripeCardModal: React.FC<StripeCardModalProps> = ({
  open,
  title,
  description,
  onDismiss,
  ...panelProps
}) => {
  const [locked, setLocked] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const { onLockChange, onDirtyChange } = panelProps;

  const handleLockChange = useCallback(
    (next: boolean) => {
      setLocked(next);
      if (onLockChange) onLockChange(next);
    },
    [onLockChange]
  );

  const handleDirtyChange = useCallback(
    (next: boolean) => {
      setDirty(next);
      if (onDirtyChange) onDirtyChange(next);
    },
    [onDirtyChange]
  );

  useEffect(() => {
    if (!open) {
      setLocked(false);
      setDirty(false);
      setConfirmDiscard(false);
    }
  }, [open]);

  const handleDismiss = () => {
    // Once an intent exists something was created at Stripe. Closing the dialog
    // would strand it and a reopen would create a second one.
    if (locked) return;
    // ASK BEFORE THROWING AWAY A TYPED CARD. The lock above only starts once the
    // card has been submitted; before that a click on the scrim silently wiped a
    // number the reader had to copy off a physical card.
    if (dirty) {
      setConfirmDiscard(true);

      return;
    }
    onDismiss();
  };
  const dialogRef = useDialogFocus<HTMLDivElement>(open, handleDismiss);
  // A modal on a desk, a sheet rising from the bottom edge on a phone — the
  // hook picks. Called unconditionally, above the early-out the Presence
  // replaces, because a hook cannot be conditional.
  const panelMotion = useOverlayMotion('modal');

  // The conditional sits INSIDE the Presence so the scrim fades and the card
  // shrinks away on close, instead of vanishing on the frame `open` flips.
  return (
    <Presence>
      {open ? (
        <ModalPortal key="stripe-card-modal">
          <Scrim {...scrimMotion} onClick={handleDismiss}>
            <Card
              {...panelMotion}
              ref={dialogRef}
              tabIndex={-1}
              role="dialog"
              aria-modal="true"
              aria-label={title}
              onClick={e => e.stopPropagation()}
            >
              <Title>{title}</Title>
              {description && <Description>{description}</Description>}
              <StripeCardPanel
                {...panelProps}
                onLockChange={handleLockChange}
                onDirtyChange={handleDirtyChange}
              />
            </Card>
          </Scrim>
          <ConfirmDialog
            open={confirmDiscard}
            title="Discard your card details?"
            message="The card details you have entered will be cleared. Nothing has been charged."
            primaryLabel="Discard"
            primaryVariant="destructive"
            dismissLabel="Keep entering"
            onPrimary={() => {
              setConfirmDiscard(false);
              onDismiss();
            }}
            onDismiss={() => setConfirmDiscard(false)}
          />
        </ModalPortal>
      ) : null}
    </Presence>
  );
};
